import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../../hooks/ApiProvider/ApiProvider";
import {
  GoogleIcon,
  AppleIcon,
  KakaoIcon,
  NaverIcon,
  FacebookIcon,
  TwitterIcon,
  GitHubIcon,
  DiscordIcon,
} from "../../../assets/icons/social";
import styles from "./Users.module.css";

interface UserInfo {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  subscription_type?: string;
  enabled: boolean;
  emailVerified: boolean;
  createdTimestamp?: number;
  attributes?: Record<string, any>;
}

interface PaginationInfo {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
  start_index: number;
  end_index: number;
}

interface SearchFilters {
  search: string;
  social_provider: string;
  enabled: string;
  has_paid_membership: string;
  subscription_type: string;
  created_after: string;
  created_before: string;
}

// 정렬 관련 인터페이스 추가
interface SortCondition {
  field: string;
  order: "asc" | "desc";
}

const Users: React.FC = () => {
  const { getUsers, toggleUserStatus, deleteUser } = useApi();
  const navigate = useNavigate();

  const [users, setUsers] = useState<UserInfo[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    search: "",
    social_provider: "",
    enabled: "true",
    has_paid_membership: "",
    subscription_type: "",
    created_after: "",
    created_before: "",
  });

  // 정렬 상태 추가
  const [sortConditions, setSortConditions] = useState<SortCondition[]>([
    { field: "createdTimestamp", order: "desc" },
  ]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 사용자 목록 조회
  const fetchUsers = useCallback(
    async (
      page: number = 1,
      filters: SearchFilters = searchFilters,
      size: number = pageSize,
      sorts: SortCondition[] = sortConditions
    ): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        const params: any = {
          page,
          page_size: size,
          ...filters,
        };

        // 빈 값들을 쿼리 파라미터에서 제거 (전체로 해석되는 값들)
        Object.keys(params).forEach((key) => {
          if (
            params[key] === "" ||
            params[key] === null ||
            params[key] === undefined ||
            (Array.isArray(params[key]) && params[key].length === 0)
          ) {
            delete params[key];
          }
        });

        // 정렬 파라미터 추가
        if (sorts.length > 0) {
          params.sort_by = sorts.map((sort) => `${sort.field}:${sort.order}`);
        }

        // 디버깅: API 호출 전 파라미터 로그
        console.log("현재 필터 상태:", filters);
        console.log("subscription_type 값:", filters.subscription_type);
        console.log("정리된 API 호출 파라미터:", params);

        const response = await getUsers(params);
        console.log("response: users", response);

        // 서버 응답 일관성 검증
        if (response.pagination) {
          if (response.pagination.current_page !== page) {
            console.warn("⚠️ 서버가 요청한 페이지와 다른 페이지를 반환했습니다!");
            console.warn(`요청: ${page}, 반환: ${response.pagination.current_page}`);
          }
        }

        // 페이징 정보 검증 및 수정
        let correctedPagination = response.pagination;
        if (response.pagination && response.users) {
          const actualTotalRecords = response.pagination.total_records;
          const actualPageSize = size;
          const calculatedTotalPages = Math.ceil(actualTotalRecords / actualPageSize);

          // 서버에서 잘못된 total_pages를 반환한 경우 수정
          if (response.pagination.total_pages !== calculatedTotalPages) {
            correctedPagination = {
              ...response.pagination,
              total_pages: calculatedTotalPages,
              has_next: calculatedTotalPages > page,
              has_previous: page > 1,
            };
          }

          // 현재 페이지가 총 페이지 수를 초과하는 경우 처리
          if (page > calculatedTotalPages && calculatedTotalPages > 0) {
            // 마지막 페이지로 이동
            const lastPage = calculatedTotalPages;
            console.log(
              `페이지 ${page}가 총 페이지 ${calculatedTotalPages}를 초과하여 마지막 페이지로 이동`
            );
            return fetchUsers(lastPage, filters, size, sorts);
          }

          // 사용자 데이터가 비어있고 총 레코드가 있는 경우 처리
          if (response.users.length === 0 && actualTotalRecords > 0 && page > 1) {
            // 이전 페이지로 이동
            const prevPage = page - 1;
            // console.log(`페이지 ${page}에서 데이터가 없어 이전 페이지 ${prevPage}로 이동`);
            return fetchUsers(prevPage, filters, size, sorts);
          }

          // 디버깅을 위한 로그 추가
          // console.log('페이징 응답:', {
          //   requestedPage: page,
          //   actualTotalRecords,
          //   actualPageSize,
          //   calculatedTotalPages,
          //   serverTotalPages: response.pagination.total_pages,
          //   usersCount: response.users.length,
          //   correctedPagination
          // });
        }

        setUsers(response.users);
        setPagination(correctedPagination);
        setCurrentPage(page);

        // 디버깅: 상태 업데이트 후 로그
        // console.log('상태 업데이트 완료:', {
        //   currentPage: page,
        //   usersCount: response.users?.length || 0,
        //   pagination: correctedPagination
        // });
      } catch (err: any) {
        console.error("fetchUsers 오류:", err);
        setError(err.message || "사용자 목록을 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    },
    [getUsers, pageSize, sortConditions]
  );

  // 초기 로드
  useEffect(() => {
    fetchUsers(1, searchFilters, pageSize, sortConditions);
  }, [fetchUsers, pageSize, sortConditions]);

  // 검색 필터 변경 핸들러
  const handleFilterChange = (key: keyof SearchFilters, value: string) => {
    console.log(`필터 변경: ${key} = ${value}`);
    setSearchFilters((prev) => {
      const newFilters = {
        ...prev,
        [key]: value,
      };
      console.log("새로운 필터 상태:", newFilters);
      return newFilters;
    });
  };

  // 검색 실행
  const handleSearch = () => {
    console.log("검색 실행 - 현재 필터 상태:", searchFilters);
    setCurrentPage(1);
    fetchUsers(1, searchFilters, pageSize, sortConditions);
  };

  // 검색 필터 초기화
  const handleResetFilters = () => {
    const resetFilters: SearchFilters = {
      search: "",
      social_provider: "",
      enabled: "true",
      has_paid_membership: "",
      subscription_type: "",
      created_after: "",
      created_before: "",
    };
    setSearchFilters(resetFilters);
    setCurrentPage(1);
    fetchUsers(1, resetFilters, pageSize, sortConditions);
  };

  // 페이지 변경
  const handlePageChange = (page: number) => {
    // console.log('=== 페이지 변경 요청 ===');
    // console.log('현재 상태:', {
    //   currentPage,
    //   pagination,
    //   searchFilters,
    //   pageSize
    // });
    // console.log('요청 페이지:', page);

    if (pagination && (page < 1 || page > pagination.total_pages)) {
      console.warn(`유효하지 않은 페이지 번호: ${page}, 총 페이지: ${pagination.total_pages}`);
      return; // 유효하지 않은 페이지 번호는 무시
    }

    // 현재 페이지와 같은 페이지로 이동하려는 경우 무시
    if (page === currentPage) {
      // console.log(`현재 페이지와 동일: ${page}`);
      return;
    }

    // console.log(`페이지 변경 실행: ${currentPage} -> ${page}`);
    // console.log('=== 페이지 변경 요청 끝 ===');
    fetchUsers(page, searchFilters, pageSize, sortConditions);
  };

  // 페이지 크기 변경
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    fetchUsers(1, searchFilters, newPageSize, sortConditions);
  };

  // 사용자 상태 변경
  const handleToggleStatus = async (userId: string, currentEnabled: boolean) => {
    try {
      await toggleUserStatus(userId, !currentEnabled);
      // 상태 변경 후 현재 페이지 새로고침
      fetchUsers(currentPage, searchFilters, pageSize, sortConditions);
    } catch (err: any) {
      setError(err.message || "사용자 상태 변경에 실패했습니다.");
    }
  };

  // 사용자 삭제
  const handleDeleteUser = async (userId: string) => {
    if (window.confirm("정말로 이 사용자를 삭제하시겠습니까?")) {
      try {
        await deleteUser(userId);
        // 삭제 후 현재 페이지 새로고침
        fetchUsers(currentPage, searchFilters, pageSize, sortConditions);
      } catch (err: any) {
        setError(err.message || "사용자 삭제에 실패했습니다.");
      }
    }
  };

  // 사용자 상세보기
  const handleViewUserDetail = (userId: string) => {
    const targetPath = `/admin/users/detail/${userId}`;

    try {
      navigate(targetPath);
    } catch (error) {
      window.location.href = targetPath;
    }
  };

  // 정렬 처리 함수
  const handleSort = (field: string) => {
    setSortConditions((prev) => {
      const newSorts = [...prev];
      const existingIndex = newSorts.findIndex((sort) => sort.field === field);

      if (existingIndex >= 0) {
        // 기존 정렬 조건이 있으면 순서 변경
        const currentOrder = newSorts[existingIndex].order;
        newSorts[existingIndex].order = currentOrder === "asc" ? "desc" : "asc";

        // 해당 필드를 맨 앞으로 이동 (최고 우선순위)
        const [removed] = newSorts.splice(existingIndex, 1);
        newSorts.unshift(removed);
      } else {
        // 새로운 정렬 조건 추가 (맨 앞에 추가하여 최고 우선순위)
        newSorts.unshift({ field, order: "asc" });
      }

      // 최대 3개까지만 유지
      if (newSorts.length > 3) {
        newSorts.splice(3);
      }

      return newSorts;
    });
  };

  // 날짜 포맷팅
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleDateString("ko-KR");
  };

  // 사용자 이름 표시
  const getUserDisplayName = (user: UserInfo) => {
    if (user.firstName && user.lastName) {
      return `${user.lastName}${user.firstName}`;
    } else if (user.firstName) {
      return user.firstName;
    } else if (user.username) {
      return user.username;
    }
    return "-";
  };

  // 사용자 이름 (name 속성)
  const getUserName = (user: UserInfo) => {
    const userName = user.attributes?.name;
    if (Array.isArray(userName)) {
      return userName[0] || "-";
    }
    return userName || "-";
  };

  // 사용자 호칭 (calledName 속성)
  const getUserCalledName = (user: UserInfo) => {
    const calledName = user.attributes?.calledName;
    if (Array.isArray(calledName)) {
      return calledName[0] || "-";
    }
    return calledName || "-";
  };

  // 소셜 프로바이더 표시
  const getSocialProvider = (user: UserInfo) => {
    // social_provider 속성에서 소셜 프로바이더 정보 가져오기
    const socialProvider = user.attributes?.social_provider;
    if (Array.isArray(socialProvider)) {
      return socialProvider[0] || "-";
    }
    return socialProvider || "-";
  };

  // 정렬 헤더 컴포넌트
  const SortableHeader = ({ field, children }: { field: string; children: React.ReactNode }) => {
    const currentSort = sortConditions.find((sort) => sort.field === field);
    const isActive = currentSort !== undefined;
    const order = currentSort?.order;

    return (
      <th
        className={`${styles.sortableHeader} ${isActive ? styles.active : ""}`}
        onClick={() => handleSort(field)}
      >
        <div className={styles.headerContent}>
          <span>{children}</span>
          <div className={styles.sortIndicator}>
            {isActive && (
              <span className={`${styles.sortArrow} ${order === "asc" ? styles.active : ""}`}>
                {order === "asc" ? "↑" : "↓"}
              </span>
            )}
            {!isActive && <span className={styles.sortPlaceholder}>↕</span>}
          </div>
        </div>
      </th>
    );
  };

  // 정렬 순서 표시
  const getSortOrderDisplay = () => {
    if (sortConditions.length === 0) return null;

    return (
      <div className={styles.sortOrderDisplay}>
        <span>정렬 순서: </span>
        {sortConditions.map((sort, index) => (
          <span key={sort.field} className={styles.sortItem}>
            {index + 1}. {getFieldDisplayName(sort.field)} {sort.order === "asc" ? "↑" : "↓"}
          </span>
        ))}
      </div>
    );
  };

  // 필드 표시명 변환
  const getFieldDisplayName = (field: string) => {
    const fieldNames: Record<string, string> = {
      username: "사용자명",
      firstName: "이름",
      lastName: "성",
      email: "이메일",
      social_provider: "소셜 로그인",
      membership_type: "멤버십",
      subscription_type: "구독 타입",
      enabled: "상태",
      createdTimestamp: "등록일",
    };
    return fieldNames[field] || field;
  };

  // 소셜 프로바이더 아이콘 컴포넌트
  const SocialProviderIcon = ({ provider }: { provider: string }) => {
    if (!provider || provider === "-") {
      return <span className={styles.socialProviderNone}>-</span>;
    }

    const providerLower = provider.toLowerCase();

    switch (providerLower) {
      case "google":
        return (
          <div className={`${styles.socialProviderIcon} ${styles.google}`}>
            <GoogleIcon width={20} height={20} />
            <span>Google</span>
          </div>
        );

      case "apple":
        return (
          <div className={`${styles.socialProviderIcon} ${styles.apple}`}>
            <AppleIcon width={20} height={20} />
            <span>Apple</span>
          </div>
        );

      case "kakao":
        return (
          <div className={`${styles.socialProviderIcon} ${styles.kakao}`}>
            <KakaoIcon width={20} height={20} />
            <span>Kakao</span>
          </div>
        );

      case "naver":
        return (
          <div className={`${styles.socialProviderIcon} ${styles.naver}`}>
            <NaverIcon width={20} height={20} />
            <span>Naver</span>
          </div>
        );

      case "facebook":
        return (
          <div className={`${styles.socialProviderIcon} ${styles.facebook}`}>
            <FacebookIcon width={20} height={20} />
            <span>Facebook</span>
          </div>
        );

      case "twitter":
        return (
          <div className={`${styles.socialProviderIcon} ${styles.twitter}`}>
            <TwitterIcon width={20} height={20} />
            <span>Twitter</span>
          </div>
        );

      case "github":
        return (
          <div className={`${styles.socialProviderIcon} ${styles.github}`}>
            <GitHubIcon width={20} height={20} />
            <span>GitHub</span>
          </div>
        );

      case "discord":
        return (
          <div className={`${styles.socialProviderIcon} ${styles.discord}`}>
            <DiscordIcon width={20} height={20} />
            <span>Discord</span>
          </div>
        );

      default:
        return (
          <div className={`${styles.socialProviderIcon} ${styles.unknown}`}>
            <span>{provider}</span>
          </div>
        );
    }
  };

  // 멤버십 타입 표시
  const getMembershipType = (user: UserInfo) => {
    // subscriptions 속성을 우선적으로 확인 (유료 회원 판단 기준)
    const subscriptions = user.attributes?.subscriptions;

    if (subscriptions) {
      // subscriptions가 배열인 경우
      if (Array.isArray(subscriptions)) {
        // 키가 있고, null이 아니고, 리스트의 길이가 0보다 크면 유료 회원
        if (subscriptions.length > 0) {
          const hasValidSubscription = subscriptions.some(
            (sub) =>
              sub !== null &&
              sub !== undefined &&
              (typeof sub === "string" ? sub.trim() !== "" : true)
          );
          if (hasValidSubscription) {
            return "paid";
          }
        }
      }
      // subscriptions가 객체인 경우
      else if (typeof subscriptions === "object" && subscriptions !== null) {
        // 객체에 값이 있으면 유료 회원
        if (Object.keys(subscriptions).length > 0) {
          return "paid";
        }
      }
      // subscriptions가 문자열인 경우 (구매코드 자체)
      else if (typeof subscriptions === "string" && subscriptions.trim() !== "") {
        return "paid";
      }
    }

    // subscriptions가 없거나 유효하지 않은 경우, 기존 membership_type 속성 확인 (하위 호환성)
    const membershipType = user.attributes?.membership_type;
    if (membershipType) {
      if (Array.isArray(membershipType)) {
        return membershipType[0] || "free";
      }
      return membershipType;
    }

    return "free";
  };

  // 멤버십 타입 표시 텍스트
  const getMembershipDisplayText = (membershipType: string) => {
    switch (membershipType) {
      case "paid":
        return "유료";
      case "premium":
        return "프리미엄";
      case "pro":
        return "프로";
      case "enterprise":
        return "엔터프라이즈";
      case "free":
        return "무료";
      case "basic":
        return "기본";
      default:
        return membershipType;
    }
  };

  // 구독 타입 표시 텍스트
  const getSubscriptionDisplayText = (subscriptionType?: string) => {
    if (!subscriptionType) return "-";

    switch (subscriptionType.toLowerCase()) {
      case "guest":
        return "손님";
      case "free":
        return "무료";
      case "digital":
        return "디지털 스토리북";
      case "premium":
        return "프리미엄 스토리북";
      default:
        return subscriptionType;
    }
  };

  // 구독 타입 CSS 클래스 반환
  const getSubscriptionBadgeClass = (subscriptionType?: string) => {
    if (!subscriptionType) return "guest";

    const type = subscriptionType.toLowerCase();
    if (["guest", "free", "digital", "premium"].includes(type)) {
      return styles[type as keyof typeof styles];
    }
    return "guest";
  };

  return (
    <div className={styles.adminUsers}>
      <h1 className="admin-page-title">사용자 관리</h1>

      {/* 검색 및 필터 섹션 */}
      <div className={styles.searchFiltersSection}>
        <div className={styles.filtersGrid}>
          {/* 검색어 */}
          <div className={styles.filterGroup}>
            <label htmlFor="search">검색어</label>
            <input
              type="text"
              id="search"
              placeholder="사용자명, 이름, 호칭, 이메일, 전화번호"
              value={searchFilters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>

          {/* 소셜 프로바이더 */}
          <div className={styles.filterGroup}>
            <label htmlFor="social_provider">소셜 프로바이더</label>
            <select
              id="social_provider"
              value={searchFilters.social_provider}
              onChange={(e) => handleFilterChange("social_provider", e.target.value)}
            >
              <option value="">전체</option>
              <option value="google">Google</option>
              <option value="apple">Apple</option>
              <option value="kakao">Kakao</option>
              <option value="naver">Naver</option>
              <option value="facebook">Facebook</option>
              <option value="twitter">Twitter</option>
              <option value="github">GitHub</option>
              <option value="discord">Discord</option>
            </select>
          </div>

          {/* 활성화 상태 */}
          <div className={styles.filterGroup}>
            <label htmlFor="enabled">계정 상태</label>
            <select
              id="enabled"
              value={searchFilters.enabled}
              onChange={(e) => handleFilterChange("enabled", e.target.value)}
            >
              <option value="">전체</option>
              <option value="true">활성</option>
              <option value="false">비활성</option>
            </select>
          </div>

          {/* 멤버십 여부 */}
          <div className={styles.filterGroup}>
            <label htmlFor="has_paid_membership">멤버십</label>
            <select
              id="has_paid_membership"
              value={searchFilters.has_paid_membership}
              onChange={(e) => handleFilterChange("has_paid_membership", e.target.value)}
            >
              <option value="">전체</option>
              <option value="true">유료</option>
              <option value="false">무료</option>
            </select>
          </div>

          {/* 구독 타입 */}
          <div className={styles.filterGroup}>
            <label htmlFor="subscription_type">구독 타입</label>
            <select
              id="subscription_type"
              value={searchFilters.subscription_type}
              onChange={(e) => handleFilterChange("subscription_type", e.target.value)}
            >
              <option value="">전체</option>
              <option value="digital">디지털</option>
              <option value="premium">프리미엄</option>
            </select>
          </div>

          {/* 등록일 시작 */}
          <div className={styles.filterGroup}>
            <label htmlFor="created_after">등록일 시작</label>
            <input
              type="date"
              id="created_after"
              value={searchFilters.created_after}
              onChange={(e) => handleFilterChange("created_after", e.target.value)}
            />
          </div>

          {/* 등록일 종료 */}
          <div className={styles.filterGroup}>
            <label htmlFor="created_before">등록일 종료</label>
            <input
              type="date"
              id="created_before"
              value={searchFilters.created_before}
              onChange={(e) => handleFilterChange("created_before", e.target.value)}
            />
          </div>
        </div>

        {/* 검색 버튼들 */}
        <div className={styles.filterActions}>
          <button className={styles.btnSearch} onClick={handleSearch} disabled={loading}>
            {loading ? "검색 중..." : "검색"}
          </button>
          <button className={styles.btnReset} onClick={handleResetFilters} disabled={loading}>
            필터 초기화
          </button>
        </div>
      </div>

      {/* 정렬 순서 표시 */}
      {/* {getSortOrderDisplay()} */}

      {/* 에러 메시지 */}
      {error && <div className={styles.errorMessage}>{error}</div>}

      {/* 페이지 크기 선택 */}
      {/* <div className={styles.pageSizeSelector}>
        <label htmlFor="pageSize">페이지당 표시:</label>
        <select
          id="pageSize"
          value={pageSize}
          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
        >
          <option value={10}>10개</option>
          <option value={20}>20개</option>
          <option value={50}>50개</option>
          <option value={100}>100개</option>
        </select>
      </div> */}

      {/* 사용자 테이블 */}
      <div className={styles.usersTableContainer}>
        {loading ? (
          <div className={styles.loadingSpinner}>로딩 중...</div>
        ) : (
          <>
            <table className={styles.usersTable}>
              <thead>
                <tr>
                  <th>번호</th>
                  <SortableHeader field="username">사용자명</SortableHeader>
                  <SortableHeader field="firstName">호칭</SortableHeader>
                  <SortableHeader field="email">이메일</SortableHeader>
                  <SortableHeader field="social_provider">소셜 로그인</SortableHeader>
                  <SortableHeader field="membership_type">멤버십</SortableHeader>
                  <SortableHeader field="subscription_type">구독 타입</SortableHeader>
                  <SortableHeader field="enabled">상태</SortableHeader>
                  <SortableHeader field="createdTimestamp">등록일</SortableHeader>
                  <th>액션</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={9} className={styles.noData}>
                      검색 결과가 없습니다.
                    </td>
                  </tr>
                ) : (
                  <>
                    {/* 실제 데이터 행들 */}
                    {users.map((user, index) => {
                      // 현재 페이지를 고려한 실제 행 번호 계산
                      const rowNumber = pagination
                        ? (pagination.current_page - 1) * pagination.page_size + index + 1
                        : index + 1;

                      return (
                        <tr key={user.id}>
                          <td>{rowNumber}</td>
                          <td className="w-[20%]">{getUserDisplayName(user)}</td>
                          <td className="w-[20%]">{getUserCalledName(user)}</td>
                          <td className="w-[20%]">{user.email || "-"}</td>
                          <td className="w-[10%]">
                            <SocialProviderIcon provider={getSocialProvider(user)} />
                          </td>
                          <td className="w-[10%]">
                            <span
                              className={`${styles.membershipBadge} ${
                                styles[getMembershipType(user) as keyof typeof styles]
                              }`}
                            >
                              {getMembershipDisplayText(getMembershipType(user))}
                            </span>
                          </td>
                          <td className="w-[10%]">
                            {(user.subscription_type === "digital" ||
                              user.subscription_type === "premium") && (
                              <span
                                className={`${styles.subscriptionBadge} ${getSubscriptionBadgeClass(
                                  user.subscription_type as string
                                )}`}
                              >
                                {getSubscriptionDisplayText(user.subscription_type)}
                              </span>
                            )}
                          </td>
                          <td className="w-[10%]">
                            <span
                              className={`${styles.statusBadge} ${
                                user.enabled ? styles.active : styles.inactive
                              }`}
                            >
                              {user.enabled ? "활성" : "비활성"}
                            </span>
                          </td>
                          <td className="w-[10%]">{formatDate(user.createdTimestamp)}</td>
                          <td className="w-[20%]">
                            <button
                              className={styles.btnViewDetail}
                              onClick={() => handleViewUserDetail(user.id)}
                            >
                              상세보기
                            </button>
                            {/* <button
                              className={`${styles.btnToggleStatus} ${user.enabled ? styles.btnDeactivate : styles.btnActivate}`}
                              onClick={() => handleToggleStatus(user.id, user.enabled)}
                            >
                              {user.enabled ? '비활성화' : '활성화'}
                            </button>
                            <button
                              className={styles.btnDelete}
                              onClick={() => handleDeleteUser(user.id)}
                            >
                              삭제
                            </button> */}
                          </td>
                        </tr>
                      );
                    })}

                    {/* 빈 행들 추가 (페이지 크기만큼 채우기) */}
                    {pagination &&
                      users.length < pagination.page_size &&
                      Array.from({ length: pagination.page_size - users.length }, (_, index) => {
                        const emptyRowNumber = pagination
                          ? (pagination.current_page - 1) * pagination.page_size +
                            users.length +
                            index +
                            1
                          : users.length + index + 1;

                        return (
                          <tr key={`empty-${index}`} className={styles.emptyRow}>
                            <td>{emptyRowNumber}</td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                          </tr>
                        );
                      })}
                  </>
                )}
              </tbody>
            </table>

            {/* 페이지네이션 */}
            {pagination && pagination.total_pages > 0 && (
              <div className={"w-full flex justify-between items-center"}>
                <div className="flex tems-center px-4">
                  {/* 페이지 크기 선택 */}
                  <div className={styles.pageSizeSelector}>
                    <select
                      id="pageSize"
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    >
                      <option value={10}>10개</option>
                      <option value={20}>20개</option>
                      <option value={50}>50개</option>
                      <option value={100}>100개</option>
                    </select>
                    <label htmlFor="pageSize">표시</label>
                  </div>
                </div>
                <div className={styles.pagination}>
                  <button
                    className={styles.btnPage}
                    disabled={!pagination.has_previous}
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                  >
                    이전
                  </button>

                  {/* 페이지 번호들 */}
                  {(() => {
                    const pages = [];
                    const totalPages = pagination.total_pages;
                    const currentPage = pagination.current_page;

                    // 페이지가 1개인 경우
                    if (totalPages === 1) {
                      pages.push(1);
                    } else {
                      // 항상 첫 페이지 표시
                      pages.push(1);

                      // 현재 페이지 주변 5개씩 표시
                      const range = 5;
                      const start = Math.max(2, currentPage - range);
                      const end = Math.min(totalPages - 1, currentPage + range);

                      // 첫 페이지와 현재 페이지 범위 사이에 간격이 있는 경우 줄임표 추가
                      if (start > 2) {
                        pages.push("...");
                      }

                      // 현재 페이지 주변 페이지들 표시
                      for (let i = start; i <= end; i++) {
                        if (i > 1 && i < totalPages) {
                          pages.push(i);
                        }
                      }

                      // 현재 페이지 범위와 마지막 페이지 사이에 간격이 있는 경우 줄임표 추가
                      if (end < totalPages - 1) {
                        pages.push("...");
                      }

                      // 마지막 페이지가 1이 아닌 경우에만 표시
                      if (totalPages > 1) {
                        pages.push(totalPages);
                      }
                    }

                    return pages.map((page, index) => (
                      <React.Fragment key={index}>
                        {page === "..." ? (
                          <span className={styles.paginationEllipsis}>...</span>
                        ) : (
                          <button
                            className={`${styles.btnPage} ${
                              page === currentPage ? styles.active : ""
                            }`}
                            onClick={() => handlePageChange(page as number)}
                            disabled={page === currentPage}
                          >
                            {page}
                          </button>
                        )}
                      </React.Fragment>
                    ));
                  })()}

                  <button
                    className={styles.btnPage}
                    disabled={!pagination.has_next}
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                  >
                    다음
                  </button>
                </div>
                <div></div>
              </div>
            )}

            {/* 페이지 정보 */}
            {pagination && pagination.total_records > 0 && (
              <div className={styles.paginationInfo}>
                총 {pagination.total_records}개 중 {pagination.start_index}-{pagination.end_index}개
                표시 (페이지 {pagination.current_page}/{pagination.total_pages})
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Users;
